import { Select } from "antd/es";
import { SelectProps } from "antd/es/select";
import { Guid } from "guid-typescript";
import _, { debounce, isEqual } from "lodash";
import moment from "moment";
import React, { CSSProperties, PureComponent, ReactNode } from "react";
import ReactDOM from "react-dom";
import { CommonProps } from "../../../components/web-components/common/common-props";
import { DropdownChevronIcon } from "../../../components/web-components/common/dropdown-chevron-icon";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import DynamicStyleManager from "../../../shared/utilty/dynamic-style-manager";
import { KMainFunctions } from "../../../shared/utilty/main-functions";
import { ReactSystemFunctions } from "../../../shared/utilty/react-system-functions";
import { KDebounceSelect } from "./k-debounceSelect";
import "./selectbox.scss";

declare let window: any;

export interface IKSelectBoxProps extends SelectProps<any> {
  datatextfield?: string;
  datavaluefield?: any;
  widthType?: "fill" | "content";
  searchable?: boolean;
  getTextFromValue?: (value: any) => void;
  onChange: (value: any) => void;
  defaultText: string;
  sortBy?: string;
  containsNullItem?: boolean;
  autoRefresh?: boolean;
  dropdownScrollItemLimit?: number;
}

interface IKSelectBoxState {
  computedOptions?: object[];
}

class KSelectBox extends PureComponent<IKSelectBoxProps & CommonProps, IKSelectBoxState> {
  // Compiled once; no `g` flag so exec stays stateless. RGBA_COLOR_REGEX groups 1-3 = r,g,b.
  private static readonly RGBA_COLOR_REGEX: RegExp = /rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*[\d.]+\s*)?\)/;
  private static readonly HEX_COLOR_REGEX: RegExp = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  private static readonly RGBA_ALPHA_REGEX: RegExp = /rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*([\d.]+)\s*\)/;

  private uniqueKey = Guid.create();

  private style = new DynamicStyleManager(`kselectbox_${this.uniqueKey}`);

  private columnTemplateCache?: { key: string; template: string; optionsRef: any; length: number };

  constructor(props: IKSelectBoxProps) {
    super(props);
    this.state = {
      computedOptions: undefined
    };
  }

  public getAllData = () => {
    return this.props.options;
  };

  componentDidMount = () => {
    this.setDynamicStyle();
    this.handleComponentStyling();
    this.bindValuesToOptionContent();
    this.calculateAndSetOptions();
    if (
      this.props.autoRefresh &&
      this.props.value === undefined &&
      this.props.onChange &&
      this.props.mode === "multiple" &&
      typeof this.props.onChange === "function"
    ) {
      this.props.onChange([]);
    }
  };

  componentDidUpdate = (prevProps: IKSelectBoxProps) => {
    const prevOptionsLength = Array.isArray(prevProps.options) ? prevProps.options.length : 0;
    const currentOptionsLength = Array.isArray(this.props.options) ? this.props.options.length : 0;

    if (
      prevProps.options !== this.props.options ||
      prevProps.datatextfield !== this.props.datatextfield ||
      prevOptionsLength !== currentOptionsLength
    ) {
      this.columnTemplateCache = undefined;
    }

    // Check if options-related props changed and recalculate if needed
    if (prevProps.options !== this.props.options) {
      this.calculateAndSetOptions();
    }
    this.handleComponentStyling();
    this.bindValuesToOptionContent();

    if (!isEqual(prevProps, this.props)) {
      this.setDynamicStyle();
    }
  };

  componentWillUnmount = () => {
    this.style.removeStyle();
    this.columnTemplateCache = undefined;
  };

  setDynamicStyle = () => {
    this.style.setDynamicCss(this.getDynamicCss());
  };

  getBgColor = (): string | undefined => {
    if (this.props.style?.backgroundColor) {
      return `background-color: ${this.props.style?.backgroundColor} !important;`;
    }
  };

  getFontSize = (): string | undefined => {
    if (this.props.style?.fontSize) {
      return `font-size: ${this.props.style?.fontSize.toString()} !important;`;
    }
  };

  getFontWeight = (): string | undefined => {
    if (this.props.style?.fontWeight) {
      return `font-weight: ${this.props.style?.fontWeight} !important;`;
    }
  };

  getColor = (): string | undefined => {
    if (this.props.style?.color) {
      return `color: ${this.props.style?.color};`;
    }
  };

  // Relative luminance (0..1) of an rgb(a)/hex color, or undefined if unparseable.
  private getLuminance = (color: string): number | undefined => {
    const rgbaMatch = KSelectBox.RGBA_COLOR_REGEX.exec(color);
    if (rgbaMatch) {
      const r: number = parseInt(rgbaMatch[1], 10);
      const g: number = parseInt(rgbaMatch[2], 10);
      const b: number = parseInt(rgbaMatch[3], 10);
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    }

    const hexMatch = KSelectBox.HEX_COLOR_REGEX.exec(color);
    if (hexMatch) {
      const hex: string = hexMatch[1];
      const r: number = parseInt(hex.length === 3 ? hex[0] + hex[0] : hex.substring(0, 2), 16);
      const g: number = parseInt(hex.length === 3 ? hex[1] + hex[1] : hex.substring(2, 4), 16);
      const b: number = parseInt(hex.length === 3 ? hex[2] + hex[2] : hex.substring(4, 6), 16);
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    }

    return undefined;
  };

  // Alpha channel (0..1) of an rgba() color, or undefined if absent.
  private getAlpha = (color: string): number | undefined => {
    const alphaMatch = KSelectBox.RGBA_ALPHA_REGEX.exec(color);
    if (alphaMatch) {
      return parseFloat(alphaMatch[1]);
    }
    return undefined;
  };

  // True only for a genuinely dark, mostly-opaque fill (needs light text). Transparent /
  // translucent / light / unset all count as light — the KOA-14744 fix (previously any
  // non-white fill forced white header text, hiding it on a white background).
  isDarkBackground = (): boolean => {
    const backgroundColor: string | undefined = this.props.style?.backgroundColor;
    if (!backgroundColor) {
      return false;
    }

    const normalized: string = backgroundColor.trim().toLowerCase();
    if (normalized === "transparent") {
      return false;
    }

    const alpha: number | undefined = this.getAlpha(normalized);
    if (alpha !== undefined && alpha < 0.5) {
      return false;
    }

    const luminance: number | undefined = this.getLuminance(normalized);
    if (luminance === undefined) {
      // Unknown/named color: keep legacy behaviour (treat explicit non-white fill as dark).
      return true;
    }

    return luminance <= 0.5;
  };

  // True when a real, non-transparent fill is set — gates the colored tag styling.
  hasCustomBackground = (): boolean => {
    const backgroundColor: string | undefined = this.props.style?.backgroundColor;
    if (!backgroundColor) {
      return false;
    }

    const normalized: string = backgroundColor.trim().toLowerCase();
    if (normalized === "transparent" || normalized === "rgba(255, 255, 255, 1)") {
      return false;
    }

    const alpha: number | undefined = this.getAlpha(normalized);
    if (alpha === 0) {
      return false;
    }

    return true;
  };

  getContrastingColor = (backgroundColor: string): string => {
    const luminance: number | undefined = this.getLuminance(backgroundColor);
    if (luminance === undefined) {
      return "#666666";
    }

    return luminance > 0.5 ? "#333333" : "#ffffff";
  };

  getDynamicCss = (): string => {
    const className: string = this.getClassName();
    if (!className || className.length === 0) {
      return "";
    }

    const bgColor = this.getBgColor();
    const color = this.getColor();
    const fontSize = this.getFontSize();
    const fontWeight = this.getFontWeight();
    const isMultiple = this.props.mode === "multiple";
    const isDark: boolean = this.isDarkBackground();
    let result = "";

    if (this.props.style?.color) {
      if (this.props.style?.color.substring(0, 4) === "rgba") {
        const match = KSelectBox.RGBA_COLOR_REGEX.exec(this.props.style?.color);
        if (match) {
          const newColor = `rgba(${[match[1], match[2], match[3], 0.15].join(",")})`;
          result += `
            .kdropdown-${className.trim()} .ant-select-item-option:hover,
            .kdropdown-${className.trim()} .ant-select-item-option-active {
              background-color: ${newColor} !important;
            }`;
        }
      }
    }

    if (bgColor || color || fontSize || fontWeight) {
      result += `.${className.trim()} > .ant-select-selector {
          ${bgColor !== undefined ? `${bgColor}` : ``}${color !== undefined ? color : ``}
        }`;

      result += `.${className.trim()} > .ant-select-arrow {
          ${bgColor !== undefined ? `${bgColor}` : ``}${color !== undefined ? color : ``}
        }`;

      result +=
        `
      .` +
        `kdropdown-${className.trim()} {
          ${bgColor !== undefined ? `${bgColor}` : ``}${color !== undefined ? color : ``}
        }`;

      result +=
        `
        .` +
        `kdropdown-${className.trim()} .ant-select-item-option {
            ${color !== undefined ? color : ``}
          }`;

      result +=
        `
        .` +
        `kdropdown-${className.trim()} .ant-select-item-option-content {
            ${fontSize !== undefined ? fontSize : ``}
            ${color !== undefined ? color : ``}
          }`;
      result +=
        `
          .` +
        `kdropdown-${className.trim()} .ant-select-item-option-content {
              ${fontWeight !== undefined ? fontWeight : ``}
            }`;
    }

    if (this.props.style?.color || this.props.style?.fontFamily || this.props.style?.fontWeight) {
      result += `
          .${className.trim()} .ant-select-selection-search-input,
          .${className.trim()} .ant-select-selection-item {
            `;
      if (this.props.style?.color && isMultiple) result += `color: ${this.props.style?.color} !important;`;
      if (this.props.style?.fontFamily) result += `font-family: ${this.props.style?.fontFamily} !important;`;
      if (this.props.style?.fontWeight) result += `font-weight: ${this.props.style?.fontWeight} !important;`;
      result += `;
      }`;
    }

    if (this.props.style?.fontSize || this.props.style?.fontFamily || this.props.style?.fontWeight) {
      result += `
          .${className.trim()} .ant-select-selection-placeholder {
            `;
      if (this.props.style?.fontSize) result += `font-size: ${this.props.style?.fontSize} !important;`;
      if (this.props.style?.fontFamily) result += `font-family: ${this.props.style?.fontFamily} !important;`;
      if (this.props.style?.fontWeight) result += `font-weight: ${this.props.style?.fontWeight} !important;`;
      result += `;
      }`;
    }

    const paddingBottom = this.props.style?.paddingBottom;
    const paddingLeft = this.props.style?.paddingLeft;
    const paddingRight = this.props.style?.paddingRight;
    const paddingTop = this.props.style?.paddingTop;
    const toCss = (v: string | number) => (typeof v === "number" ? `${v}px` : v);
    const inlineStart = paddingLeft != undefined ? toCss(paddingLeft) : "unset";
    const inlineEnd = paddingRight != undefined ? toCss(paddingRight) : "unset";

    result += `.${className.trim()} .ant-select-selector {
          `;
    if (paddingBottom != undefined) result += `padding-bottom: ${toCss(paddingBottom)} !important;`;
    if (paddingLeft != undefined) result += `padding-left: ${toCss(paddingLeft)} !important;`;
    if (paddingRight != undefined) result += `padding-right: ${toCss(paddingRight)} !important;`;
    if (paddingTop != undefined) result += `padding-top: ${toCss(paddingTop)} !important;`;
    result += `;
      }`;

    result += `
          .${className.trim()} .ant-select-selection-placeholder {
            `;
    result += `color: #bfbfbf !important;`;
    result += `;
      }`;

    result += `
    .${className.trim()} .ant-select-selection-search-input {
      height: 100% !important;
    }`;

    result += `
    .${className.trim()} .ant-select-selection-search {
      margin-left: 0 !important;
      inset-inline-start: ${inlineStart} !important;
      inset-inline-end: ${inlineEnd} !important;
    }`;

    result += `
    .${className.trim()} .ant-select-selection-placeholder {
      inset-inline-start: ${inlineStart} !important;
      inset-inline-end: ${inlineEnd} !important;
      line-height: inherit !important;
    }`;

    result += `
    .${className.trim()} .ant-select-selector:after {
      line-height: inherit !important;
      margin: 0 !important;
    }`;

    result += `
    .${className.trim()} .ant-select-selection-item {
      inset-inline-start: ${inlineStart} !important;
      inset-inline-end: ${inlineEnd} !important;
      line-height: inherit !important;
      margin: 0 !important;
    }`;

    result += `
    .kdropdown-${className.trim()} .ant-select-item-option.k-selectbox-null-item .ant-select-item-option-content {
      color: #bfbfbf !important;
    }`;

    result += `
    .kdropdown-${className.trim()} .ant-select-item-option.k-selectbox-null-item.ant-select-item-option-active .ant-select-item-option-content,
    .kdropdown-${className.trim()} .ant-select-item-option.k-selectbox-null-item:hover .ant-select-item-option-content {
      color: #bfbfbf !important;
    }`;

    result += `
    .${className.trim()} .ant-select-selection-item[title="${this.props.placeholder ?? ""}"] {
      color: #bfbfbf !important;
    }`;

    result += `
    .${className.trim()} .ant-select-selection-item[value="null"] {
      color: #bfbfbf !important;
    }`;

    result += `
        .kdropdown-${className.trim()} .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
          background: ${isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(68,68,68,0.15)"}  !important;
        }

        /* Table layout styles for multi-field display */

        .kdropdown-${className.trim()} .k-selectbox-table-header-wrapper {
          padding: 4px 12px;
          width: 100%;
          box-sizing: border-box;
          transition: padding-right 0.1s ease;
        }

        /* Add padding when scrollbar is present */
        .kdropdown-${className.trim()}.has-scrollbar .k-selectbox-table-header-wrapper {
          padding-right: 24px; /* 12px base + 12px scrollbar */
        }

        .kdropdown-${className.trim()} .k-selectbox-table-header {
          display: flex;
          align-items: center;
          gap: 0px;
          width: 100%;
          min-width: 100%;
          background-color: ${isDark ? "rgba(255, 255, 255, 0.1)" : "#fafafa"};
          border-bottom: 1px solid ${isDark ? "rgba(255, 255, 255, 0.2)" : "#e8e8e8"};
          font-weight: 600;
          font-size: 12px;
          color: ${isDark ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.65)"};
          margin-bottom: 4px;
        }

        .kdropdown-${className.trim()} .k-selectbox-table-header-cell {
          box-sizing: border-box;
          padding: 4px 8px;
          border-right: 1px solid ${isDark ? "rgba(255, 255, 255, 0.15)" : "#f0f0f0"};
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .kdropdown-${className.trim()} .k-selectbox-table-header-cell {
          flex: 1;
        }

        .kdropdown-${className.trim()} .k-selectbox-table-header-cell:last-child {
          border-right: none;
        }

        .kdropdown-${className.trim()} .k-selectbox-table-wrapper {
          display: flex;
          width: 100%;
          box-sizing: border-box;
        }

        .kdropdown-${className.trim()} .k-selectbox-table-row {
          display: flex;
          align-items: center;
          gap: 0px;
          width: 100%;
          min-width: 100%;
        }

        .kdropdown-${className.trim()} .k-selectbox-table-cell {
          box-sizing: border-box;
          padding: 4px 8px;
          border-right: 1px solid ${isDark ? "rgba(255, 255, 255, 0.15)" : "#f0f0f0"};
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-size: ${this.props.style?.fontSize || 14}px;
          ${this.props.style?.fontWeight ? `font-weight: ${this.props.style.fontWeight};` : ""}
          ${this.props.style?.fontFamily ? `font-family: ${this.props.style.fontFamily};` : ""}
        }

        .kdropdown-${className.trim()} .k-selectbox-table-cell {
          flex: 1;
        }

        .kdropdown-${className.trim()} .k-selectbox-table-cell:last-child {
          border-right: none;
        }

        .kdropdown-${className.trim()} .ant-select-item-option:hover .k-selectbox-table-cell {
          border-right-color: ${isDark ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.1)"};
        }

        .kdropdown-${className.trim()} .ant-select-item-option-selected .k-selectbox-table-cell {
          border-right-color: ${isDark ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.1)"};
        }

        /* Single select multi-field display styling */
        .${className.trim()} .ant-select-selection-item:has(.k-selectbox-table-wrapper) {
          padding: 0 !important;
          margin: 0 !important;
          max-width: none !important;
          overflow: visible !important;
          width: 100%;
          height: 100%;
          display: flex;
          background: ${isDark ? "transparent" : "white"} !important;
          border: none !important;
        }

        .${className.trim()} .ant-select-selection-item .k-selectbox-table-wrapper {
          display: flex;
          width: 100%;
          overflow-x: auto;
          overflow-y: hidden;
          box-sizing: border-box;
        }

        .${className.trim()} .ant-select-selection-item .k-selectbox-table-row {
          display: flex !important;
          align-items: center;
          gap: 0px;
          width: 100%;
          margin: 0;
          padding: 0;
        }

        .${className.trim()} .ant-select-selection-item .k-selectbox-table-cell {
          box-sizing: border-box;
          padding: 4px 8px !important;
          border-right: 1px solid ${isDark ? "rgba(255, 255, 255, 0.15)" : "#f0f0f0"};
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-size: ${this.props.style?.fontSize || 14}px;
          ${this.props.style?.fontWeight ? `font-weight: ${this.props.style.fontWeight};` : ""}
          ${this.props.style?.fontFamily ? `font-family: ${this.props.style.fontFamily};` : ""}
          color: ${isDark ? "rgba(255, 255, 255, 0.9)" : "inherit"} !important;
          flex: 1;
        }

        .${className.trim()} .ant-select-selection-item .k-selectbox-table-cell:last-child {
          border-right: none;
        }
        `;

    result += ` .${className.trim()} .ant-select-selection-item-remove {
     display: flex !important;
     align-items: center !important;
     justify-content: center !important;
    }`;

    result += `
    .${className.trim()} .ant-select-selection-overflow {
     gap: 8px !important;
    }`;

    if (bgColor && this.hasCustomBackground() && this.props.style?.backgroundColor) {
      const contrastingColor = this.getContrastingColor(this.props.style.backgroundColor);
      result += `
    .${className.trim()} .ant-select-selection-item {
      background-color: ${isMultiple ? "transparent" : this.props.style.backgroundColor} !important;
    }`;

      if (isMultiple) {
        result += `
        .${className.trim()} .ant-select-selection-item {
            border: 1px solid ${this.props.style.borderColor || contrastingColor} !important;
        }`;
      }

      result += `
    .${className.trim()} .ant-select-selection-item-content {
      color: ${isMultiple ? this.props.style?.color : this.props.style?.backgroundColor} !important;
    }`;

      if (isMultiple && this.props.style?.color !== "rgba(68, 68, 68, 1)") {
        result += `
    .${className.trim()} .ant-select-selection-item-content {
      color: ${this.props.style?.color} !important;
    }`;
      }

      result += `
    .${className.trim()} .ant-select-selection-item-remove {
      color: ${this.props.style?.borderColor || contrastingColor} !important;
    }`;
    }

    if ((this.props.style?.height || this.props.style?.minHeight || this.props.style?.maxHeight) && isMultiple) {
      result += `
        .${className.trim()} .ant-select-selector {
          ${this.props.style?.height !== undefined ? `height: ${this.props.style?.height} !important;` : ``}
          ${this.props.style?.minHeight !== undefined ? `min-height: ${this.props.style?.minHeight} !important;` : ``}
          ${this.props.style?.maxHeight !== undefined ? `max-height: ${this.props.style?.maxHeight} !important;` : ``}
          overflow-y: auto !important;
          overflow-x: hidden !important;
        }`;

      result += `
    .${className.trim()} .ant-select-selector:after {
      line-height: 0 !important;
    }`;
    }

    result += `
    .${className.trim()} .ant-select-clear {
      background: transparent !important;
      opacity: 1 !important;
      margin-right: 20px !important;
      margin-top: -8px !important;
    }`;

    result += `
    .${className.trim()} .ant-select-arrow svg {
      transition: transform 0.2s ease;
    }`;

    result += `
    .${className.trim()}.ant-select-open .ant-select-arrow svg {
      transform: rotate(180deg);
    }`;

    return result;
  };

  getClassName = () => {
    let result = "";
    if (this.props.className) {
      result = this.props.className;
    }
    if (!this.uniqueKey) {
      return result;
    }
    result = `${result} kselectbox_${this.uniqueKey.toString().substring(0, 8)}`;
    return result;
  };

  getWidth() {
    if (this.props.widthType && this.props.widthType == "content") {
      return "max-content";
    }
    return "100%";
  }

  private getColumnTemplate = (fieldNames: string[]): string => {
    if (!fieldNames || !fieldNames.length) {
      return "120px";
    }

    const cacheKey = fieldNames.join("|");
    const optionsLength = Array.isArray(this.props.options) ? this.props.options.length : 0;
    if (
      this.columnTemplateCache &&
      this.columnTemplateCache.key === cacheKey &&
      this.columnTemplateCache.optionsRef === this.props.options &&
      this.columnTemplateCache.length === optionsLength
    ) {
      return this.columnTemplateCache.template;
    }

    const template = fieldNames.map(() => "1fr").join(" ");

    this.columnTemplateCache = {
      key: cacheKey,
      template,
      optionsRef: this.props.options,
      length: optionsLength
    };

    return template;
  };

  private getRowStyle = (columnTemplate: string): CSSProperties => {
    return {
      display: "grid",
      gridTemplateColumns: columnTemplate,
      alignItems: "center",
      gap: 0,
      width: "100%"
    };
  };

  private getFieldValue = (row: any, fieldName: string): string => {
    if (!row || !fieldName) {
      return "";
    }

    const actualFieldName = Object.keys(row).find((key) => key.toLowerCase() === fieldName.toLowerCase());

    if (!actualFieldName) {
      return "";
    }

    const value = row[actualFieldName];

    if (value === undefined || value === null) {
      return "";
    }

    if (Number.isNaN(Number(value)) && typeof value === "string") {
      const formatParsedDate = (parsedDate: moment.Moment): string => {
        const mlValue = localStorage.getItem("ml") || "";
        const userLocale = mlValue ? mlValue.split("_")[0] : "";
        moment.locale(userLocale);
        const appDateFormat = ReactSystemFunctions.getAppDateFormat();
        const appTimeFormat = ReactSystemFunctions.getAppTimeFormat();
        const hasTime: boolean = parsedDate.hours() !== 0 || parsedDate.minutes() !== 0 || parsedDate.seconds() !== 0;

        if (hasTime) {
          if (appDateFormat && appTimeFormat) {
            const momentDateFormat = appDateFormat.replace(/dd/g, "DD").replace(/mm/g, "MM").replace(/yyyy/g, "YYYY");
            const momentTimeFormat = appTimeFormat
              .replace(/hh/g, "HH")
              .replace(/mm/g, "mm")
              .replace(/ss/g, "ss")
              .replace(/a/g, "A");
            return parsedDate.format(`${momentDateFormat} ${momentTimeFormat}`);
          }
          return parsedDate.format(
            `${moment.localeData().longDateFormat("L")} ${moment.localeData().longDateFormat("LT")}`
          );
        }

        if (appDateFormat) {
          const momentDateFormat = appDateFormat.replace(/dd/g, "DD").replace(/mm/g, "MM").replace(/yyyy/g, "YYYY");
          return parsedDate.format(momentDateFormat);
        }
        return parsedDate.format(moment.localeData().longDateFormat("L"));
      };
      const isoParsed = moment(value, moment.ISO_8601, true);
      if (isoParsed.isValid()) {
        return formatParsedDate(isoParsed);
      }
      const dateFormats = [
        "DD.MM.YYYY HH:mm:ss",
        "D.MM.YYYY HH:mm:ss",
        "DD.M.YYYY HH:mm:ss",
        "D.M.YYYY HH:mm:ss",
        "MM/DD/YYYY HH:mm:ss",
        "DD/MM/YYYY HH:mm:ss",
        "M/DD/YYYY HH:mm:ss",
        "DD/M/YYYY HH:mm:ss",
        "DD.MM.YYYY HH:mm",
        "D.MM.YYYY HH:mm",
        "MM/DD/YYYY HH:mm",
        "DD/MM/YYYY HH:mm",
        "DD.MM.YYYY",
        "D.MM.YYYY",
        "MM/DD/YYYY",
        "DD/MM/YYYY"
      ];
      const parsed = moment(value, dateFormats, true);
      if (parsed.isValid()) {
        return formatParsedDate(parsed);
      }
    }

    const formattedValue = ReactSystemFunctions.convertToTypeByName(value, "string");

    if (formattedValue === undefined || formattedValue === null) {
      return "";
    }

    return String(formattedValue);
  };

  private handleDropdownVisibleChange = (open: boolean) => {
    if (open) {
      const SCROLLBAR_THRESHOLD: number = 6;
      const hasScrollbar: boolean = this.props.options && this.props.options.length > SCROLLBAR_THRESHOLD;

      if (hasScrollbar) {
        this.style.setDynamicCss(
          `${this.getDynamicCss()}.kdropdown-${this.getClassName().trim()} .k-selectbox-table-header-wrapper {
            padding-right: 24px !important;
          }`
        );
      }
    }
    if (this.props.onDropdownVisibleChange) {
      this.props.onDropdownVisibleChange(open);
    }
  };

  handleComponentStyling = () => {
    if (!this.props.style) return;
    const { style } = this.props;
    const node = ReactDOM.findDOMNode(this) as Element;
    const item = node.querySelector(".ant-select-selector") as HTMLElement;
    const input = node.querySelector(".ant-select-selection-search-input") as HTMLElement;
    const isMultiple = node?.classList?.contains("ant-select-multiple");
    if (input) {
      input.setAttribute("focusid", this.props.id);
    }
    if (!isMultiple) {
      // Vertically center the placeholder / selected value. align-items is not an
      // inherited property, so without setting it the content sticks to the top
      // whenever the selector grows past its content height (e.g. min-height:40px
      // with auto height from padding). Previously this was only set when a
      // fontSize/height style was provided, leaving style-less selectboxes top-aligned.
      item.style.alignItems = "center";
      if (this.props.style?.height) {
        this.handleHeight(item);
      } else {
        item.style.height = "40px";
      }

      if (this.props.style?.minHeight) {
        this.handleMinHeight(item);
      } else if (this.props.style?.height) {
        item.style.minHeight = this.props.style?.height as string;
      } else {
        item.style.minHeight = "40px";
      }

      if (this.props.style?.maxHeight) {
        this.handleMaxHeight(item);
      }
    }
    if (this.props.style?.color) {
      this.handleColor(node, style);
    }
    if (this.props.style?.fontSize) {
      this.handleFontSize(node);
    }
    if (this.props.style?.fontWeight) {
      this.handleFontWeight(item, style);
    }

    this.handleBorderRadius(item, style);
    this.handleBorder(item, style);
    this.handleBoxShadow(item, style);
    this.handlePadding(item, style);
  };

  handleHeight = (item: HTMLElement) => {
    item.style.height = this.props.style!.height as any;
    item.style.alignItems = "center";
  };

  handleMinHeight = (item: HTMLElement) => {
    item.style.minHeight = this.props.style!.minHeight as any;
  };

  handleMaxHeight = (item: HTMLElement) => {
    item.style.maxHeight = this.props.style!.maxHeight as any;
  };

  handleFontWeight = (item: HTMLElement, style: React.CSSProperties) => {
    if (style?.fontWeight && item && item.style) {
      item.style.fontWeight = this.props.style!.fontWeight.toString();
    }
  };

  handleBorderRadius = (item: HTMLElement, style: React.CSSProperties) => {
    if (style?.borderRadius !== undefined) {
      item.style.borderRadius = `${style.borderRadius.toString()}px` as any;
    }
    if (style?.borderTopLeftRadius !== undefined) {
      item.style.borderTopLeftRadius = `${style.borderTopLeftRadius.toString()}px` as any;
    }
    if (style?.borderTopRightRadius !== undefined) {
      item.style.borderTopRightRadius = `${style.borderTopRightRadius.toString()}px` as any;
    }
    if (style?.borderBottomLeftRadius !== undefined) {
      item.style.borderBottomLeftRadius = `${style.borderBottomLeftRadius.toString()}px` as any;
    }
    if (style?.borderBottomRightRadius !== undefined) {
      item.style.borderBottomRightRadius = `${style.borderBottomRightRadius.toString()}px` as any;
    }
  };

  handleBorder = (item: HTMLElement, style: React.CSSProperties) => {
    if (style?.border) {
      item.style.border = style.border as any;
    }
    if (style?.borderRadius) {
      item.style.borderRadius = style.borderRadius as any;
    }
    if (style?.borderWidth) {
      item.style.borderWidth = style.borderWidth as any;
    }
    if (style?.borderColor) {
      item.style.borderColor = style.borderColor as any;
    }
    if (style?.borderStyle) {
      item.style.borderStyle = style.borderStyle as any;
    }
    if (style?.borderTopWidth != undefined) {
      item.style.borderTopWidth = `${style.borderTopWidth.toString()}px` as any;
    }
    if (style?.borderRightWidth != undefined) {
      item.style.borderRightWidth = `${style.borderRightWidth.toString()}px` as any;
    }
    if (style?.borderBottomWidth != undefined) {
      item.style.borderBottomWidth = `${style.borderBottomWidth.toString()}px` as any;
    }
    if (style?.borderLeftWidth != undefined) {
      item.style.borderLeftWidth = `${style.borderLeftWidth.toString()}px` as any;
    }
  };

  handleBoxShadow = (item: HTMLElement, style: React.CSSProperties) => {
    if (style?.boxShadow) {
      item.style.boxShadow = style.boxShadow as string;
    }
  };

  handlePadding = (item: HTMLElement, style: React.CSSProperties) => {
    if (style?.paddingTop != undefined) {
      item.style.paddingTop = `${style.paddingTop.toString()}px`;
    }
    if (style?.paddingRight != undefined) {
      item.style.paddingRight = `${style.paddingRight.toString()}px`;
    }
    if (style?.paddingBottom != undefined) {
      item.style.paddingBottom = `${style.paddingBottom.toString()}px`;
    }
    if (style?.paddingLeft != undefined) {
      item.style.paddingLeft = `${style.paddingLeft.toString()}px`;
    }

    // When vertical padding is set without an explicit height, use auto height
    // so content + padding determine total size (matching NumberInput/Input behavior)
    if ((style?.paddingTop != undefined || style?.paddingBottom != undefined) && !style?.height) {
      item.style.height = "auto";
      item.style.minHeight = "40px";
    }
  };

  handleColor = (node: Element, style: React.CSSProperties) => {
    const selectionItem: HTMLElement = node.getElementsByClassName("ant-select-selection-item")[0] as HTMLElement;
    if (selectionItem) {
      selectionItem.style.color = style?.color as any;
    }
  };

  handleFontSize = (node: Element) => {
    const item = node.firstChild as HTMLElement;
    item.style.fontSize = this.props.style!.fontSize as any;
    item.style.alignItems = "center";
  };

  getProps = () => {
    const isDesignTime = window.kuika?.isDesignTime;
    const isMultiField = this.props.datatextfield?.includes(",");
    const props: any = {};
    props.datatextfield = this.props.datatextfield;
    props.datavaluefield = this.props.datavaluefield;
    props.widthType = this.props.widthType;
    props.searchable = this.props.searchable;
    props.options = this.props.options;
    props.onChange = this.props.onChange ?? _.noop;
    props.placeholder = this.props.placeholder;
    props.onBlur = isDesignTime ? () => {} : (this.props.onBlur ?? _.noop);
    props.className = this.props.className;
    props.allowClear = this.props.allowClear;
    props.showSearch = this.props.showSearch;
    props.suffixIcon = this.getSuffixIcon();
    props.mode = this.props.mode;
    // Ant Design 5 defaults `showArrow` to false in "multiple" mode, which suppresses
    // the suffixIcon entirely — so the unified dropdown chevron vanishes on multi-selects.
    // Force it on so single- and multi-select render the same chevron.
    if (this.props.mode === "multiple") {
      props.showArrow = true;
    }
    props.listHeight = this.props.dropdownScrollItemLimit ? this.props.dropdownScrollItemLimit * 32 : undefined;
    if (props.listHeight && isMultiField) {
      props.listHeight += 25; // extra space for header
    }
    if (typeof this.props.maxTagCount == "number" && this.props.maxTagCount >= 0) {
      props.maxTagCount = this.props.maxTagCount;
    }
    if (this.props.tabIndex !== undefined) {
      props.tabIndex = this.props.tabIndex;
    }

    let style: any | undefined = _.clone(this.props.style);
    if (props.options) {
      delete props.options;
    }
    if (!style) {
      style = {
        width: this.getWidth()
      };
    } else if (!style.width) {
      style.width = this.getWidth();
    }
    if (style) {
      style.border = "none";
      delete style.padding;
      delete style.paddingTop;
      delete style.paddingRight;
      delete style.paddingBottom;
      delete style.paddingLeft;
      delete style.borderLeftWidth;
      delete style.borderBottomWidth;
      delete style.borderRightWidth;
      delete style.borderTopWidth;
      delete style.borderTopLeftRadius;
      delete style.borderTopRightRadius;
      delete style.borderBottomLeftRadius;
      delete style.borderBottomRightRadius;
      delete style.backgroundColor;
      delete style.boxShadow;

      if (style.display == "block") {
        style.display = "flex";
      } else if (style.display == "inline") {
        style.display = "inline-flex";
      }
    }
    props.style = style;
    if (props.value == "") {
      delete props.value;
    }
    if (props.value) {
      delete props.value;
    }
    return props;
  };

  calculateAndSetOptions = () => {
    const computedOptions = this.computeOptions();
    this.setState({ computedOptions });
  };

  computeOptions = (): object[] | undefined => {
    const isDesignTime = window.kuika?.isDesignTime;
    const dataTextField = this.props.datatextfield;
    const dataValueField = this.props.datavaluefield;

    if (dataTextField && dataValueField && dataTextField.length > 1 && dataValueField.length > 1) {
      const sourceOptions = this.props.options;
      if (!sourceOptions || !sourceOptions.length) {
        if (this.props.containsNullItem) {
          return [
            {
              label: this.props.placeholder ?? "",
              value: null,
              key: "null",
              className: "k-selectbox-null-item",
              "data-value": null
            }
          ];
        }
        return [];
      }
      const isMultiField = dataTextField.includes(",");
      const fieldNames = isMultiField ? dataTextField.split(",").map((f) => f.trim()) : [dataTextField];
      const columnTemplate = this.getColumnTemplate(fieldNames);
      const rowStyle = this.getRowStyle(columnTemplate);
      const options: any[] = [];
      const useSort = this.props.sortBy === "A-Z" || this.props.sortBy === "Z-A";
      const sortAscending = this.props.sortBy === "A-Z";
      const sortKeys: string[] = [];
      for (let index = 0; index < sourceOptions.length; index++) {
        const row = sourceOptions[index];
        if (_.isUndefined(row)) {
          continue;
        }
        const rowValue = row[dataValueField];
        if (rowValue === null || rowValue === undefined) {
          continue;
        }
        let label: any;
        let sortKey = "";
        if (isMultiField) {
          const cells: JSX.Element[] = [];
          for (let idx = 0; idx < fieldNames.length; idx++) {
            const fieldName = fieldNames[idx];
            const fieldValue = this.getFieldValue(row, fieldName);
            if (idx === 0) {
              sortKey = fieldValue;
            }
            cells.push(
              <div key={idx} className="k-selectbox-table-cell" title={fieldValue}>
                {fieldValue}
              </div>
            );
          }
          label = (
            <div className="k-selectbox-table-wrapper">
              <div className="k-selectbox-table-row" style={rowStyle}>
                {cells}
              </div>
            </div>
          );
        } else {
          const fieldValue = row[dataTextField!];
          if (_.isUndefined(fieldValue)) {
            continue;
          }
          label = fieldValue;
          sortKey = fieldValue;
        }
        const normalizedSortKey = String(sortKey ?? "");
        const option: any = {
          label,
          value: rowValue,
          key: this.props.searchable ? rowValue : `${rowValue}_${index}`,
          "data-value": rowValue,
          "data-is-multi-field": isMultiField
        };
        if (useSort) {
          let low = 0;
          let high = sortKeys.length;
          while (low < high) {
            const mid = (low + high) >>> 1;
            const compare = normalizedSortKey.localeCompare(sortKeys[mid]);
            if (sortAscending ? compare >= 0 : compare <= 0) {
              low = mid + 1;
            } else {
              high = mid;
            }
          }
          options.splice(low, 0, option);
          sortKeys.splice(low, 0, normalizedSortKey);
        } else {
          options.push(option);
        }
      }
      if (this.props.containsNullItem) {
        const nullOption = {
          label: this.props.placeholder ?? "",
          value: null,
          key: "null",
          className: "k-selectbox-null-item",
          "data-value": null
        };
        if (useSort) {
          options.unshift(nullOption);
          sortKeys.unshift("");
        } else {
          options.unshift(nullOption);
        }
      }
      return options;
    }
    if (isDesignTime) {
      const designOptions: any[] = [];
      if (this.props.options && this.props.options.length) {
        this.props.options.forEach((element) => {
          designOptions.push({
            label: element.text ?? element.value,
            value: element.text ?? element.value,
            key: element.key ?? element.value,
            "data-value": element.value
          });
        });
      }
      return designOptions;
    }

    return this.props.options;
  };

  getOptions = (): object[] | undefined => {
    return this.state.computedOptions ?? [];
  };

  getFormValue = () => {
    return this.props.value;
  };

  // Custom render function for selected values in multi-field mode
  tagRender = (props: any) => {
    const { label, value, closable, onClose } = props;
    const dataTextField = this.props.datatextfield;

    // Check if this is multi-field mode
    if (dataTextField && dataTextField.includes(",")) {
      const fieldNames = dataTextField.split(",").map((f) => f.trim());
      const selectedRow = this.props.options?.find((row) => row[this.props.datavaluefield!] == value);

      if (selectedRow) {
        const columnTemplate = this.getColumnTemplate(fieldNames);
        const rowStyle = this.getRowStyle(columnTemplate);
        const isDarkBg: boolean = this.isDarkBackground();
        return (
          <span
            className="ant-select-selection-item"
            style={{
              display: "inline-block",
              backgroundColor: isDarkBg ? "rgba(255, 255, 255, 0.1)" : "#f6f6f6",
              border: isDarkBg ? "1px solid rgba(255, 255, 255, 0.2)" : "1px solid #d9d9d9",
              borderRadius: "6px",
              padding: "0",
              margin: "2px 4px 2px 0",
              maxWidth: "none",
              overflow: "visible",
              position: "relative"
            }}
          >
            <div
              className="k-selectbox-table-wrapper"
              style={{
                display: "block",
                overflowX: "auto",
                width: "100%"
              }}
            >
              <div className="k-selectbox-table-row" style={rowStyle}>
                {fieldNames.map((fieldName, idx) => {
                  const fieldValue = this.getFieldValue(selectedRow, fieldName);

                  return (
                    <div
                      key={idx}
                      className="k-selectbox-table-cell"
                      style={{
                        color: isDarkBg ? "rgba(255, 255, 255, 0.9)" : undefined
                      }}
                    >
                      {fieldValue}
                    </div>
                  );
                })}
              </div>
            </div>
            {closable && (
              <span
                className="ant-select-selection-item-remove"
                onClick={onClose}
                style={{
                  position: "absolute",
                  top: "-8px",
                  right: "-8px",
                  width: "16px",
                  height: "16px",
                  backgroundColor: "#ff4d4f",
                  color: "white",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px",
                  cursor: "pointer",
                  lineHeight: "1",
                  border: "1px solid white",
                  zIndex: 1
                }}
              >
                ×
              </span>
            )}
          </span>
        );
      }
    }

    // Default single-field rendering
    return (
      <span className="ant-select-selection-item">
        {label}
        {closable && (
          <span className="ant-select-selection-item-remove" onClick={onClose}>
            ×
          </span>
        )}
      </span>
    );
  };

  getValue = (options: any[]) => {
    const sourceOptions = options ?? (this.getOptions() as any[]);
    if (this.props.mode == "multiple") {
      if (this.props.value === "") {
        return [];
      }
      if (typeof this.props.value === "string") {
        return this.props.value.split(",");
      }
      return this.props.value;
    }
    if (this.props.searchable !== true) {
      let value;
      if (sourceOptions && sourceOptions.length) {
        sourceOptions.forEach((element) => {
          if (element.value == this.props.value || element.value == this.props.defaultValue) {
            // 2 eşittir olacak
            value = element.value;
          }
        });
      }
      return value;
    }

    if (window.kuika?.dashboardState === 1 && (this.props.value === "" || this.props.value === undefined)) {
      // Designer
      return undefined;
    }

    let { defaultText } = this.props;
    if (this.pressedClear === true) {
      defaultText = "";
    }
    let value;
    let text: undefined | string;
    if (sourceOptions && sourceOptions.length) {
      sourceOptions.forEach((element) => {
        if (element && element.value && element.value == this.props.value) {
          // 2 eşittir olacak
          value = element;
          text = element.label;
        }
      });
    }
    if (text !== undefined && this.props.value && this.refreshCounter === 2) {
      this.refreshCounter = 1;
      this.searchValue = "";
      this.handleChange({ key: this.props.value, label: text, value: this.props.value });
    }
    if (value === undefined && this.props.searchable === true) {
      if (
        this.props.value != undefined &&
        this.props.value.toString().length > 0 &&
        this.props.getTextFromValue &&
        (this.props.defaultText === undefined || this.props.defaultText === "") &&
        this.refreshCounter === 0
      ) {
        this.refreshCounter++;
        this.props.getTextFromValue(this.props.value);
      }
    }
    if (text === undefined || text === "") {
      if (
        defaultText &&
        defaultText.length > 0 &&
        this.refreshCounter === 1 &&
        (this.searchValue === undefined || this.searchValue === "")
      ) {
        this.refreshCounter++;
        this.waitAndSearch(defaultText);
      }
      text = defaultText;
    }
    if (this.props.value == undefined && text == undefined) {
      return undefined;
    }
    if (
      this.props.searchable === true &&
      (this.props.value === "" ||
        this.props.value === null ||
        this.props.value === undefined ||
        !this.props.value?.value) &&
      text === undefined
    ) {
      return null;
    }
    return { key: this.props.value, label: text, value: this.props.value };
  };

  refreshCounter: number = 0;

  searchValue = "";

  pressedClear: boolean = false;

  getSearchValue = () => {
    return this.searchValue;
  };

  waitAndSearch = debounce((value) => this.handleSearch(value), 50);

  handleSearch = (value) => {
    this.pressedClear = false;
    this.searchValue = value;
    if (this.props.onSearch) {
      this.props.onSearch(value);
    }
  };

  handleGetTextFromValue = (value: any) => {
    if (this.props.getTextFromValue) {
      this.props.getTextFromValue(value);
    }
  };

  handleChange = (value: any) => {
    const isDesignTime = window.kuika?.isDesignTime;
    if (value === undefined) {
      this.searchValue = "";
      this.pressedClear = true;
      if (this.props.onChange) {
        this.props.onChange(undefined);
      }
    }
    if (this.props.mode == "multiple") {
      if (isDesignTime) return;
      this.props.onChange(value);
    } else if (value !== undefined && value.key && this.props.onChange) {
      this.props.onChange(value.key);
    } else if (value !== undefined && this.props?.onChange) {
      if (isDesignTime) return;
      this.props.onChange(value);
    }

    this.bindValuesToOptionContent();
  };

  bindValuesToOptionContent = () => {
    setTimeout(() => {
      const options = this.getOptions() as any[];
      if (!options || !options.length) return;

      const optionContentElements = document.querySelectorAll(".ant-select-item-option-content");
      optionContentElements.forEach((element) => {
        const parent = element.closest(".ant-select-item-option") as HTMLElement;
        if (parent) {
          const optionValue = parent.getAttribute("title");
          if (optionValue) {
            element.setAttribute("title", optionValue);
          }
        }
      });
    }, 100);
  };

  filterOption = (input: string, option: any) => {
    if (!input) return true;

    const normalizedInput = KMainFunctions.turkishCharsToEnglish(input.toLowerCase());

    // Handle multi-field options (React elements)
    if (option?.["data-is-multi-field"] && typeof option.label === "object") {
      // For multi-field options, search in all table cell values
      const dataTextField = this.props.datatextfield;
      if (dataTextField && dataTextField.includes(",")) {
        const fieldNames = dataTextField.split(",").map((f) => f.trim());
        const rowData = this.props.options?.find((row) => row[this.props.datavaluefield!] === option.value);

        if (rowData) {
          return fieldNames.some((fieldName) => {
            const fieldValue = this.getFieldValue(rowData, fieldName);
            const normalizedFieldValue = KMainFunctions.turkishCharsToEnglish(fieldValue.toLowerCase());
            return normalizedFieldValue.includes(normalizedInput);
          });
        }
      }
      return false;
    }

    // Handle single-field options (strings)
    if (!option?.label) return true;
    const optionText = typeof option.label === "string" ? option.label : "";
    const normalizedOption = KMainFunctions.turkishCharsToEnglish(optionText.toLowerCase());
    return normalizedOption.includes(normalizedInput);
  };

  hasClassNames = (element: any, classNames: string[]): boolean => {
    if (!element || !element.className) return false;

    return classNames.some(
      (className) => typeof element.className === "string" && element.className.includes(className)
    );
  };

  toPascalCase = (value: string): string => {
    const camel = KMainFunctions.toCamelCase(value);
    return camel.charAt(0).toUpperCase() + camel.slice(1);
  };

  getClearIcon = () => {
    const iconColor = this.props.style?.color || "#606F85";
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <g clipPath="url(#clip0_15729_7457)">
          <path d="M12 4L4 12" stroke={iconColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M4 4L12 12" stroke={iconColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </g>
        <defs>
          <clipPath id="clip0_15729_7457">
            <rect width="16" height="16" fill="white" />
          </clipPath>
        </defs>
      </svg>
    );
  };

  getSuffixIcon = (): JSX.Element => {
    const iconColor: string = (this.props.style?.color as string) || "#606F85";
    return <DropdownChevronIcon color={iconColor} />;
  };

  render(): ReactNode {
    const dataTextField = this.props.datatextfield;
    const isMultiField = typeof dataTextField === "string" && dataTextField.includes(",");
    const fieldNames = isMultiField ? dataTextField.split(",").map((f) => f.trim()) : [];
    const columnTemplate = isMultiField ? this.getColumnTemplate(fieldNames) : "";
    const userDropdownRender = this.props.dropdownRender;
    const dropdownRenderer = (menu: ReactNode) => {
      const renderedMenu =
        typeof userDropdownRender === "function" ? userDropdownRender(menu as React.ReactElement) : menu;
      if (!isMultiField || !fieldNames.length) {
        return renderedMenu;
      }

      const headerStyle = this.getRowStyle(columnTemplate);
      return (
        <div className="k-selectbox-table-scroll-container">
          <div className="k-selectbox-table-header-wrapper">
            <div className="k-selectbox-table-header" style={headerStyle}>
              {fieldNames.map((name, idx) => (
                <div key={idx} className="k-selectbox-table-header-cell">
                  {this.toPascalCase(name)}
                </div>
              ))}
            </div>
          </div>
          {renderedMenu}
        </div>
      );
    };
    const options = this.getOptions() as any;

    return (
      <>
        {this.props.searchable === true ? (
          <KDebounceSelect
            value={this.getValue(options)}
            dropdownClassName={`kdropdown-${this.getClassName().trim()}`}
            {...this.getProps()}
            options={this.getOptions() as any}
            onSearchValueChange={this.handleSearch}
            getTextFromValue={this.handleGetTextFromValue}
            className={`k-selectbox-component ${this.getClassName()}`}
            onChange={this.handleChange}
            dropdownMatchSelectWidth={false}
            clearIcon={this.getClearIcon()}
            data-id={this.props.id}
          />
        ) : (
          <Select
            filterOption={this.filterOption}
            showSearch={this.props.showSearch}
            value={this.getValue(options)}
            {...this.getProps()}
            options={this.getOptions() as any}
            className={`k-selectbox-component ${this.getClassName()}`}
            popupClassName={`kdropdown-${this.getClassName().trim()}`}
            onChange={this.handleChange}
            onDropdownVisibleChange={this.handleDropdownVisibleChange}
            clearIcon={this.getClearIcon()}
            data-id={this.props.id}
            getPopupContainer={(triggerNode) => {
              let parent = triggerNode.parentNode;

              const bodyReturnClasses = ["rc-tabs-tabpane"];
              const returnParentClasses = ["ant-space-item"];

              let currentLevel = 0;
              let bodyClassFound = false;
              let tempParent = parent;

              while (parent && parent !== document.body) {
                if (this.hasClassNames(parent, returnParentClasses)) {
                  return parent;
                }

                if (parent instanceof HTMLElement) {
                  const style = window.getComputedStyle(parent);
                  if (
                    style.overflow === "auto" ||
                    style.overflow === "scroll" ||
                    style.overflowY === "auto" ||
                    style.overflowY === "scroll"
                  ) {
                    return triggerNode.parentNode;
                  }
                }
                parent = parent.parentNode;
              }

              while (tempParent && tempParent !== document.body && currentLevel < 20) {
                if (this.hasClassNames(tempParent, bodyReturnClasses)) {
                  bodyClassFound = true;
                  return document.body;
                }
                tempParent = tempParent.parentNode;
                currentLevel++;
              }

              return triggerNode.parentNode;
            }}
            dropdownRender={dropdownRenderer}
          ></Select>
        )}
      </>
    );
  }
}

const kSelectBox = withCommonEvents(KSelectBox);
export { kSelectBox as KSelectBox };
